import type { PlainDateString } from "@sa-nu/shared-utils/date";
import { genPlainDateList, PlainDate } from "@sa-nu/shared-utils/date";
import { assertNever } from "@sa-nu/shared-utils/misc";
import clsx from "clsx";
import Image from "next/image";

import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { isCICOStayNight } from "../_utils/cico";
import { useCICOModal } from "../ci-co-date-selection-modal";
import {
  reservationCalendarGridColumnStyles,
  type CICOSelection,
  type ReservationCalendarNavigationSize,
} from "../reservation-calendar";

import { resolveDateTapAction } from "./_utils/resolve-date-tap-action";
import type { DateCellDateType } from "./date-cell";
import { DateCell } from "./date-cell";
import { NavigationButton } from "./navigation-button";

export function DateNavigation({
  firstNight,
  lastNight,
  minNight,
  maxNight,
  getDateType,
  isPeakDay,
  navigationSize,
  isInternal = false,
  onChevronNavigate,
  cico,
  onClearCICO,
  onCICOSelected,
}: {
  firstNight: PlainDate;
  lastNight: PlainDate;
  minNight: PlainDate;
  maxNight: PlainDate;
  getDateType: (dateString: PlainDateString | PlainDate) => DateCellDateType;
  isPeakDay: (date: PlainDate) => boolean;
  navigationSize: ReservationCalendarNavigationSize;
  isInternal?: boolean;
  onChevronNavigate: (deltaDays: number) => void;
  cico: CICOSelection | null;
  onClearCICO: () => void;
  onCICOSelected: (cico: CICOSelection) => void;
}) {
  const dates = genPlainDateList(firstNight, lastNight);
  const { open: openCICOModal } = useCICOModal();

  const gtm = useGtm();

  const handleChevron = (deltaDays: number) => {
    gtm.sendEvent("rsv_cal_nav_chevron_tapped", {});
    onChevronNavigate(deltaDays);
  };

  const handleDateCellTap = (date: PlainDate) => {
    const action = resolveDateTapAction(date, cico, navigationSize);
    if (action !== "ignore") {
      gtm.sendEvent("rsv_cal_date_picked");
    }
    switch (action) {
      case "set":
        onCICOSelected({ checkIn: date, checkOut: date.add({ days: 1 }) });
        break;
      case "expandCheckIn":
        onCICOSelected({
          checkIn: cico!.checkIn.subtract({ days: 1 }),
          checkOut: cico!.checkOut,
        });
        break;
      case "shrinkCheckIn":
        onCICOSelected({
          checkIn: cico!.checkIn.add({ days: 1 }),
          checkOut: cico!.checkOut,
        });
        break;
      case "expandCheckOut":
        onCICOSelected({
          checkIn: cico!.checkIn,
          checkOut: cico!.checkOut.add({ days: 1 }),
        });
        break;
      case "shrinkCheckOut":
        onCICOSelected({
          checkIn: cico!.checkIn,
          checkOut: cico!.checkOut.subtract({ days: 1 }),
        });
        break;
      case "clear":
        onClearCICO();
        break;
      case "ignore":
        break;
      default:
        assertNever(action);
    }
  };

  return (
    <>
      <div className="border-skogecha/10 bg-slightbeige flex h-[48px] w-full items-center justify-between border-b px-[16px] py-[8px]">
        <div className="flex items-center gap-[8px]">
          <NavigationButton
            data-analytics-id="reservation_calendar_clicked_previous_week"
            disabled={PlainDate.compare(firstNight, minNight) <= 0}
            onClick={() => handleChevron(-navigationSize)}
          >
            <Image
              src="/i/icon_chevron_double_left.svg"
              alt="前の週"
              width={16}
              height={16}
            />
          </NavigationButton>
          <NavigationButton
            data-analytics-id="reservation_calendar_clicked_previous_day"
            disabled={PlainDate.compare(firstNight, minNight) <= 0}
            onClick={() => handleChevron(-1)}
          >
            <Image
              src="/i/icon_chevron_left_no_padding.svg"
              alt="前日"
              width={6}
              height={10}
            />
          </NavigationButton>
        </div>
        <div
          data-analytics-id="reservation_calendar_opened_date_picker"
          onClick={openCICOModal}
          className="border-skogecha bg-swhite flex h-[32px] items-center justify-center gap-[4px] rounded-full border px-[16px] text-xs font-medium hover:cursor-pointer hover:opacity-70"
        >
          {/* 一番近い印で打ち切るので、ignore にすると外側の div の id まで届かない */}
          <button
            data-analytics-id="reservation_calendar_opened_date_picker"
            type="button"
            className="flex min-w-0 flex-1 items-center justify-center gap-[8px]"
          >
            <Image
              src="/i/calendar.svg"
              alt="calendar"
              width={16}
              height={17.6}
            />
            {cico
              ? `${cico.checkIn.month}/${cico.checkIn.day}〜${cico.checkOut.month}/${cico.checkOut.day}`
              : "日付で探す"}
          </button>
          {cico && (
            <button
              data-analytics-id="reservation_calendar_clicked_date_clear"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearCICO();
              }}
              className="text-skogecha flex size-[24px] shrink-0 items-center justify-center rounded-full hover:opacity-70"
              aria-label="チェックイン・チェックアウト日のクリア"
            >
              <Image src="/i/icon_close_x.svg" alt="" width={16} height={16} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[8px]">
          <NavigationButton
            data-analytics-id="reservation_calendar_clicked_next_day"
            disabled={PlainDate.compare(lastNight, maxNight) >= 0}
            onClick={() => handleChevron(1)}
          >
            <Image
              src="/i/icon_chevron_right_no_padding.svg"
              alt="翌日"
              width={6}
              height={10}
            />
          </NavigationButton>
          <NavigationButton
            data-analytics-id="reservation_calendar_clicked_next_week"
            disabled={PlainDate.compare(lastNight, maxNight) >= 0}
            onClick={() => handleChevron(navigationSize)}
          >
            <Image
              src="/i/icon_chevron_double_right.svg"
              alt="次の週"
              width={16}
              height={16}
            />
          </NavigationButton>
        </div>
      </div>
      <div
        className={clsx(
          "border-oldsgray bg-swhite grid h-[46px] grid-rows-1 border-b",
          reservationCalendarGridColumnStyles[navigationSize].columns,
        )}
      >
        <div className="border-oldsgray bg-slightbeige-116 font-notojp text-skogecha flex h-[46px] w-full items-center justify-center border-r text-[14px] leading-4 font-bold">
          日付
        </div>
        {dates.map((date, index) => {
          const dateType = getDateType(date);
          const isCICOHighlight = isCICOStayNight(date, cico);
          return (
            <DateCell
              key={index}
              date={date}
              dateType={dateType}
              isCICOHighlight={isCICOHighlight}
              isInternal={isInternal}
              onClick={() => handleDateCellTap(date)}
            />
          );
        })}
      </div>
      <div
        className={clsx(
          "bg-slightbeige-116 grid h-[20px] md:h-[22px]",
          reservationCalendarGridColumnStyles[navigationSize].columns,
        )}
      >
        <div />
        {dates.map((date) => (
          <div
            key={date.toString()}
            className="flex items-center justify-center"
          >
            {isPeakDay(date) && (
              <span className="bg-skogecha font-notojp text-slightbeige flex h-[12px] w-full max-w-[42px] items-center justify-center rounded-[2px] text-[8px] leading-none font-bold tracking-[0.16px] md:h-[14px] md:text-[10px]">
                繁忙日
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
